import useReferrerNameAccounts from 'src/hooks/useReferrerNameAccounts';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import useReferralRewardStructure from 'src/hooks/useReferralRewardStructure';
import { getReferralLink } from 'src/utils/referral';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useAppInitialLoad from 'src/hooks/app/useAppInitialLoad';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { useShallow } from 'zustand/react/shallow';
import useAuthorityReferralSnapshots from 'src/hooks/query/snapshots/authority/useAuthorityReferralSnapshots';
import { useMemo } from 'react';
import { ReferralTimePeriodOption } from 'src/components/ReferralsPageContent/consts';
import { ReferralChartPoint } from 'src/components/ReferralsPageContent/types';

const DAYS_BY_PERIOD: Record<ReferralTimePeriodOption, number> = {
	'7d': 7,
	'30d': 30,
	'90d': 90,
};

interface Params {
	period: ReferralTimePeriodOption;
}

export default function useReferralsPageData({ period }: Params) {
	const [accounts, userAccountNotInitialized] = useDriftAccountStore(
		useShallow((s) => [s.accounts, s.userAccountNotInitialized])
	);
	const { referrerNameAccounts } = useReferrerNameAccounts();
	const { referrerEarnPercent } = useReferralRewardStructure();
	const isWalletConnected = useWalletIsConnected();
	const { isInitialLoading } = useAppInitialLoad();

	const {
		data,
		isLoading: isReferralSnapshotsLoading,
		isFetching,
	} = useAuthorityReferralSnapshots({
		days: DAYS_BY_PERIOD[period],
		authority: referrerNameAccounts.accounts[0]?.authority.toString(),
	});

	const sortedSnapshots = useMemo(() => {
		if (!data?.data.snapshots) {
			return [];
		}

		return [...data.data.snapshots].sort((a, b) => a.ts - b.ts);
	}, [data]);

	const { rewardsChartData, numReferralsChartData } = useMemo(() => {
		const rewards: ReferralChartPoint[] = [];
		const numReferrals: ReferralChartPoint[] = [];

		sortedSnapshots.forEach(({ ts, referralRewards, referralCount }, index) => {
			const date = new Date(ts * 1000);

			const prevSnapshot = sortedSnapshots[index - 1];

			rewards.push({
				date,
				// Fall back to previous rewards value if it exists, otherwise fall back
				// to `0` as a last resort.
				value: referralRewards ?? prevSnapshot?.referralRewards ?? 0,
			});

			const currReferralCount = referralCount ?? 0;
			const prevReferralCount =
				prevSnapshot?.referralCount ?? currReferralCount;

			numReferrals.push({
				date,
				value: currReferralCount - prevReferralCount,
			});
		});

		return { rewardsChartData: rewards, numReferralsChartData: numReferrals };
	}, [sortedSnapshots]);

	const earliestSnapshot = sortedSnapshots[0];
	const latestSnapshot = sortedSnapshots[sortedSnapshots.length - 1];

	const rewardsDeltaForPeriod = sortedSnapshots.length
		? latestSnapshot.referralRewards - earliestSnapshot.referralRewards
		: 0;

	const totalRewards = BigNum.fromPrint(
		(latestSnapshot?.referralRewards ?? 0).toString(),
		QUOTE_PRECISION_EXP
	);

	const numReferralsDeltaForPeriod = useMemo(
		() => numReferralsChartData.reduce((total, curr) => total + curr.value, 0),
		[numReferralsChartData]
	);

	const totalNumReferrals = BigNum.fromPrint(
		(latestSnapshot?.referralCount ?? 0).toString()
	);

	const referralLink = getReferralLink(
		referrerNameAccounts.accounts[0]?.decodedName
	);

	const referralLinkExistsForAccount = referrerNameAccounts.accounts.length > 0;

	const firstAccountExists = !!Object.values(accounts).find(
		(acc) => acc?.userId === 0
	);

	const deletedFirstAccount =
		isWalletConnected && !userAccountNotInitialized && !firstAccountExists;

	const isAccountDataLoading =
		(isWalletConnected && !userAccountNotInitialized) || isInitialLoading;

	const isReferralDataLoading =
		isReferralSnapshotsLoading ||
		referrerNameAccounts.loading ||
		!referrerNameAccounts.initialReferrerNameAccountsLoaded;

	const isLoading = isAccountDataLoading && isReferralDataLoading;

	return {
		totalRewards,
		totalNumReferrals,
		rewardsChartData,
		rewardsDeltaForPeriod,
		numReferralsChartData,
		numReferralsDeltaForPeriod,
		referrerEarnPercent,
		referralLink,
		referralLinkExistsForAccount,
		deletedFirstAccount,
		isLoading,
		isFetching,
	};
}
